import React, { useState, useEffect } from 'react';
import { fetchFinancialDataPriceIntraday } from '../../../../services/financialDataService';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import styles from './FinancialData.module.css';
import CustomDropdown from './CustomDropdownIntraday';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, ArcElement);

function FinancialDataIntraDay({ companyData }) {
  const [financialData, setFinancialData] = useState({});
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState({});
  const [metricSelection, setMetricSelection] = useState('');
  const [dataInterval, setDataInterval] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [shouldPlot, setShouldPlot] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [activeMonthLabel, setActiveMonthLabel] = useState('');


  const metricOptions = {
    TIME_SERIES_INTRADAY: [
      "4. close",
      "1. open",
      "2. high",
      "3. low",
      "5. volume"
    ]
  };

  const intervals = ['1min', '5min', '15min', '30min', '60min'];

  const handleButtonClick = async () => {
    if (!companyData?.symbol || !dataInterval) return;
  
    setLoading(true);
    setErrorMsg('');
    setActiveMonthLabel('');
  
    let monthParam = undefined;
    let displayMonth = '';
  
    if (startDate) {
      // ✅ Use startDate and ignore endDate
      monthParam = startDate.slice(0, 7); // 'YYYY-MM'
      const [year, month] = monthParam.split('-');
      displayMonth = new Date(`${month}/01/${year}`).toLocaleString('default', {
        month: 'long',
        year: 'numeric'
      });
    } else if (endDate) {
      // ✅ Use endDate only if startDate is empty
      monthParam = endDate.slice(0, 7);
      const [year, month] = monthParam.split('-');
      displayMonth = new Date(`${month}/01/${year}`).toLocaleString('default', {
        month: 'long',
        year: 'numeric'
      });
    }
  
    const data = await fetchFinancialDataPriceIntraday(
      'TIME_SERIES_INTRADAY',
      companyData.symbol,
      dataInterval,
      'full',
      monthParam // ✅ month only passed when valid
    );
  
    if (data?.['Error Message'] || data?.Note) {
      setErrorMsg(
        "Sorry, data for the selected period is not available. Please try a different time interval."
      );
      setFinancialData({});
      setShouldPlot(false);
    } else {
      setFinancialData(data);
      setShouldPlot(true);
  
      // ✅ Friendly label based on date selection
      if (displayMonth) {
        setActiveMonthLabel(`Data shown for: ${displayMonth}`);
      } else {
        setActiveMonthLabel('Data shown for: most recent month');
      }
    }
  
    setLoading(false);
  };
  
  

  useEffect(() => {
    if (!shouldPlot || !metricSelection) return;

    const series = financialData['Time Series (' + dataInterval + ')'] || {};
    let reports = Object.entries(series).map(([date, values]) => ({
      ...values,
      timestamp: date
    }));

    if (reports.length > 0) {
        reports = reports.filter(report => {
          const reportDate = new Date(report.timestamp);
          const start = startDate
          ? new Date(startDate)
          : endDate
            ? new Date(endDate.slice(0, 7) + '-01T00:00:00')
            : null;
        
          const end = endDate ? new Date(endDate + 'T23:59:59') : null;
      
          return (!start || reportDate >= start) &&
                 (!end || reportDate <= end);
    });
      

      const labels = reports.map(r => r.timestamp).reverse();
      const dataPoints = reports.map(r => parseFloat(r[metricSelection]) || 0).reverse();

      const datasets = {
        label: metricSelection.replace(/^\d+\.\s/, ''),
        data: dataPoints,
        fill: false,
        backgroundColor: 'rgb(75, 192, 192)',
        borderColor: 'rgb(75, 192, 192)',
        pointRadius: 0
      };

      setChartData({ labels, datasets: [datasets] });
    }
  }, [metricSelection, financialData, dataInterval, startDate, endDate, shouldPlot]);

  return (
    <div className={styles.container}>

      <div className={styles.chartControls}>
        <CustomDropdown
          options={metricOptions['TIME_SERIES_INTRADAY']}
          onSelect={setMetricSelection}
          value={metricSelection}
          placeholder="Select a Metric"
          resetTrigger={dataInterval}
        />

        <CustomDropdown
          options={intervals}
          onSelect={setDataInterval}
          value={dataInterval}
          placeholder="Select Interval"
          resetTrigger={'interval-change'}
        />

        <div className={styles.dateInputs}>
          <div>
            <label>From:</label>
            <input type="date" onChange={(e) => setStartDate(e.target.value)} value={startDate} />
          </div>
          <div>
            <label>To:</label>
            <input type="date" onChange={(e) => setEndDate(e.target.value)} value={endDate} />
          </div>
        </div>

        {(metricSelection && dataInterval) && (
          <div className={styles.buttonContainer}>
            <button className={styles.button} onClick={handleButtonClick}>
              Fetch Intraday Data
            </button>
          </div>
        )}
      </div>
      {errorMsg && (
        <div className={styles.errorMsg}>
            {errorMsg}
        </div>
        )}

      {loading ? (
        <p>Loading...</p>
      ) : (
        metricSelection && Object.keys(chartData).length > 0 &&
        <div className={styles.chartContainer}>
          <div className={styles.scrollableChart}>
            <div className={styles.chartContent}>
             {activeMonthLabel && (
                <div className={styles.monthLabel}>
                <p>{activeMonthLabel}</p>
                </div>
             )}
             <Line
                data={chartData}
                options={{
                  responsive: true,
                  scales: {
                    x: {
                      ticks: { maxRotation: 0, autoSkipPadding: 20 }
                    }
                  },
                  plugins: {
                    tooltip: {
                      bodyFont: { size: window.innerWidth < 768 ? 12 : 14 },
                      titleFont: { size: window.innerWidth < 768 ? 12 : 14 }
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FinancialDataIntraDay;
